/**
 * 设备检测工具函数
 * 用于检测用户设备类型，支持微信登录的设备适配
 */

export interface DeviceInfo {
  /** 是否为移动设备 */
  isMobile: boolean;
  /** 是否在微信内置浏览器中 */
  isWeChat: boolean;
  /** User-Agent 字符串 */
  userAgent: string;
  /** 屏幕宽度 */
  screenWidth: number;
}

/** 移动设备 User-Agent 关键词 */
const MOBILE_UA_KEYWORDS = [
  "Android",
  "webOS",
  "iPhone",
  "iPad",
  "iPod",
  "BlackBerry",
  "IEMobile",
  "Opera Mini",
  "Mobile",
  "mobile",
  "CriOS",
  "FxiOS",
];

/** 移动设备屏幕宽度阈值 */
const MOBILE_SCREEN_WIDTH_THRESHOLD = 768;

/**
 * 检测是否为移动设备
 * 综合 User-Agent 和屏幕宽度判断
 */
export function isMobileDevice(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || "";
  const screenWidth = window.innerWidth || window.screen?.width || 0;

  // User-Agent 包含移动设备关键词
  const isMobileUA = MOBILE_UA_KEYWORDS.some((keyword) =>
    userAgent.includes(keyword)
  );

  // 屏幕宽度小于阈值
  const isSmallScreen = screenWidth < MOBILE_SCREEN_WIDTH_THRESHOLD;

  // 综合判断：User-Agent 匹配或屏幕宽度小于阈值
  return isMobileUA || isSmallScreen;
}

/**
 * 检测是否在微信内置浏览器中
 */
export function isWeChatBrowser(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || "";
  return /MicroMessenger/i.test(userAgent);
}

/**
 * 获取完整的设备信息
 */
export function detectDevice(): DeviceInfo {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      isMobile: false,
      isWeChat: false,
      userAgent: "",
      screenWidth: 0,
    };
  }

  return {
    isMobile: isMobileDevice(),
    isWeChat: isWeChatBrowser(),
    userAgent: navigator.userAgent || "",
    screenWidth: window.innerWidth || window.screen?.width || 0,
  };
}

/**
 * 根据 User-Agent 字符串判断是否为移动设备
 * 用于测试和服务端渲染场景
 */
export function isMobileByUserAgent(userAgent: string): boolean {
  if (!userAgent) return false;
  return MOBILE_UA_KEYWORDS.some((keyword) => userAgent.includes(keyword));
}

/**
 * 根据屏幕宽度判断是否为移动设备
 */
export function isMobileByScreenWidth(screenWidth: number): boolean {
  return screenWidth > 0 && screenWidth < MOBILE_SCREEN_WIDTH_THRESHOLD;
}
